import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import { PhUser, PhStar, PhCalendarCheck, PhChatCircle, PhCertificate } from 'ember-phosphor-icons';

/**
 * Coach profile information
 */
export interface Coach {
  id: string;
  name: string;
  title: string;
  photo?: string;
  specialties: string[];
  rating: number;
  reviewCount: number;
  yearsExperience: number;
  credentials: string[];
  availableSlots: number;
  bio: string;
}

/**
 * CoachCard - Displays a coach profile card
 *
 * Shows coach information including photo, name, specialties,
 * credentials, ratings, and availability with booking CTA.
 *
 * @example
 * ```gts
 * <CoachCard
 *   @coach={{this.coach}}
 *   @onSchedule={{this.openScheduler}}
 * />
 * ```
 */
interface CoachCardSignature {
  Args: {
    /** Coach profile data */
    coach: Coach;
    /** Handler for schedule button click */
    onSchedule?: (coach: Coach) => void;
    /** Whether to show compact view */
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

// Helper to render star ratings
const renderStars = (rating: number) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const stars = [];

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push('full');
    } else if (i === fullStars && hasHalfStar) {
      stars.push('half');
    } else {
      stars.push('empty');
    }
  }
  return stars;
};

const CoachCard: TOC<CoachCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <div
    class="tio-bg-surface rounded-2xl shadow-sm ring-1 ring-gray-950/[0.04] overflow-hidden hover:shadow-lg transition-all duration-300"
    ...attributes
  >
    {{! Header with gradient background }}
    <div class="tio-gradient-brand p-6 tio-fg-inverse relative overflow-hidden">
      <div class="absolute inset-0 bg-black/5"></div>
      <div class="relative tio-row-loose">
        {{! Photo or placeholder }}
        <div
          class="w-20 h-20 rounded-full bg-white/20 tio-row-center overflow-hidden ring-4 ring-white/30"
        >
          {{#if @coach.photo}}
            <img
              src={{@coach.photo}}
              alt="{{@coach.name}}"
              class="w-full h-full object-cover"
              loading="lazy"
            />
          {{else}}
            <PhUser @size={{40}} class="text-white/80" />
          {{/if}}
        </div>

        <div class="flex-1">
          <Heading @level={{3}} class="tio-fg-inverse mb-1">
            {{@coach.name}}
          </Heading>
          <Text @size="sm" class="text-violet-100">
            {{@coach.title}}
          </Text>

          {{! Rating }}
          <div class="tio-row-tight mt-2">
            <div class="flex items-center gap-0.5">
              {{#each (renderStars @coach.rating) as |star|}}
                {{#if (eq star "full")}}
                  <PhStar @size={{14}} @weight="fill" class="text-amber-300" />
                {{else if (eq star "half")}}
                  <PhStar @size={{14}} @weight="fill" class="text-amber-300 opacity-50" />
                {{else}}
                  <PhStar @size={{14}} class="text-white/40" />
                {{/if}}
              {{/each}}
            </div>
            <Text @size="xs" class="text-violet-100">
              {{@coach.rating}}
              ({{@coach.reviewCount}}
              reviews)
            </Text>
          </div>
        </div>
      </div>
    </div>

    {{! Body content }}
    <div class="p-6 space-y-4">
      {{! Experience & Credentials }}
      <div class="tio-row-loose text-sm tio-fg-secondary">
        <div class="tio-row-extra-tight">
          <PhCertificate @size={{16}} class="tio-fg-brand" />
          <span>{{@coach.yearsExperience}}+ years</span>
        </div>
        <div class="tio-row-extra-tight">
          <PhCalendarCheck @size={{16}} class="tio-fg-success" />
          <span>{{@coach.availableSlots}} slots this week</span>
        </div>
      </div>

      {{! Specialties }}
      <div class="tio-wrap-tight">
        {{#each @coach.specialties as |specialty|}}
          <Chip class="text-xs tio-bg-brand-soft tio-fg-brand">
            {{specialty}}
          </Chip>
        {{/each}}
      </div>

      {{! Bio preview }}
      {{#unless @compact}}
        <Text @size="sm" class="tio-fg-secondary line-clamp-2">
          {{@coach.bio}}
        </Text>
      {{/unless}}

      {{! Credentials }}
      <div class="pt-2 border-t tio-border-subtle">
        <Text @size="xs" class="tio-fg-muted mb-2">
          Credentials
        </Text>
        <div class="tio-wrap-extra-tight">
          {{#each @coach.credentials as |credential|}}
            <span class="text-xs px-2 py-0.5 tio-bg-sunken tio-fg-secondary rounded">
              {{credential}}
            </span>
          {{/each}}
        </div>
      </div>

      {{! Actions }}
      <div class="tio-flex pt-2">
        {{#if @onSchedule}}
          <Button
            @intent="primary"
            @appearance="outlined"
            class="flex-1"
            {{on "click" (fn @onSchedule @coach)}}
          >
            <PhCalendarCheck @size={{16}} class="mr-2" />
            Schedule
          </Button>
        {{else}}
          <Button @intent="primary" @appearance="outlined" class="flex-1">
            <PhCalendarCheck @size={{16}} class="mr-2" />
            Schedule
          </Button>
        {{/if}}
        <Button @intent="primary" @appearance="minimal" class="flex-1">
          <PhChatCircle @size={{16}} class="mr-2" />
          Message
        </Button>
      </div>
    </div>
  </div>
</template>;

export default CoachCard;
